import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileJson, FileSpreadsheet, FileText, LayoutGrid, Upload } from 'lucide-react';
import {
  CSV_FIELD_LABELS,
  isKabaBoardFile,
  parseCsvImport,
  parseTableImport,
  parseTextImport,
  planPreview,
  readXlsx,
  XlsxError,
  templateSummary,
  type Board,
  type KabaBoardFile,
  type ParsedImport,
} from '@kaba/shared';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { toast } from '../../lib/toast';
import type { Target } from '../../lib/structure';
import { ImportTrelloForm } from '../ImportTrello';
import { Button, cx } from '../ui/ui';
import { BoardPreview } from './BoardPreview';
import { fieldClass, openNewBoard, TargetFields, useBoardTarget } from './target';

export type ImportTab = 'text' | 'csv' | 'trello' | 'kaba';

const TABS: { id: ImportTab; label: string; icon: ReactNode }[] = [
  { id: 'text', label: 'Aufgaben als Text', icon: <FileText size={16} /> },
  { id: 'csv', label: 'Excel / Planner / CSV', icon: <FileSpreadsheet size={16} /> },
  { id: 'trello', label: 'Trello', icon: <LayoutGrid size={16} /> },
  { id: 'kaba', label: 'KaBa-Datei', icon: <FileJson size={16} /> },
];

const EXAMPLE = `# Sommerfest 2027
## Offen
- Getränke bestellen #Einkauf @15.6.
  [ ] Wasser und Saft
  [ ] Kaffee
- Plakat gestalten #Werbung
  Format A3, Logo vom Verein
- Helferliste aufstellen
## In Arbeit
- Zelt reservieren #Einkauf
## Erledigt
[x] Termin festlegen`;

type PlanSource = 'text' | 'csv' | 'xlsx' | 'planner' | 'asana' | 'jira' | 'todoist' | 'ticktick';
interface Contact {
  id: string;
  name: string;
  email: string;
}

const n = (x: number, one: string, many: string) => `${x.toLocaleString('de-DE')} ${x === 1 ? one : many}`;

/** Excel speichert CSV oft als Windows-1252 – erst UTF-8 versuchen */
export async function readText(f: File): Promise<string> {
  const buf = await f.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return new TextDecoder('windows-1252').decode(buf);
  }
}

export function ImportPanel({ open, tab, onTab, target, onClose }: { open: boolean; tab: ImportTab; onTab: (t: ImportTab) => void; target?: Target | null; onClose: () => void }) {
  return (
    <div className="flex flex-col gap-4">
      <div role="tablist" aria-label="Importieren aus" className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            id={`imp-tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`imp-panel-${t.id}`}
            onClick={() => onTab(t.id)}
            className={cx(
              'inline-flex min-h-11 shrink-0 items-center gap-2 rounded-lg px-3 text-sm font-medium sm:min-h-10',
              tab === t.id ? 'bg-accent text-on-accent' : 'bg-black/[0.04] hover:bg-black/[0.07] dark:bg-white/5 dark:hover:bg-white/10',
            )}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`imp-panel-${tab}`} aria-labelledby={`imp-tab-${tab}`}>
        {tab === 'text' && <TextImport open={open} target={target} onClose={onClose} />}
        {tab === 'csv' && <CsvImport open={open} target={target} onClose={onClose} />}
        {tab === 'trello' && <ImportTrelloForm open={open} onClose={onClose} target={target} />}
        {tab === 'kaba' && <KabaImport open={open} target={target} onClose={onClose} />}
      </div>
    </div>
  );
}

/* ---------- Gemeinsamer Abschluss: Vorschau, Name, Ablage ---------- */

function PlanResult({
  parsed,
  source,
  defaultTitle,
  open,
  target,
  onClose,
  extra,
}: {
  parsed: ParsedImport;
  source: PlanSource;
  defaultTitle: string;
  open: boolean;
  target?: Target | null;
  onClose: () => void;
  extra?: ReactNode;
}) {
  const t = useBoardTarget(open, target);
  const [title, setTitle] = useState(defaultTitle);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!touched) setTitle(defaultTitle);
  }, [defaultTitle, touched]);
  const { plan, warnings } = parsed;
  const preview = useMemo(() => planPreview(plan, 8), [plan]);
  const { data: me } = useMe();
  const { data: contacts } = useQuery({
    queryKey: ['import-contacts'],
    queryFn: () => api.get<Contact[]>('/api/import/contacts'),
    enabled: open && plan.members.length > 0,
  });
  // Personen aus der Datei: gleiche Namen (oder E-Mail-Adressen) automatisch zuordnen, Rest wählt man selbst
  const [map, setMap] = useState<Record<string, string>>({});
  useEffect(() => {
    const auto: Record<string, string> = {};
    for (const m of plan.members) {
      const k = m.name.trim().toLowerCase();
      const hit = contacts?.find((c) => c.name.trim().toLowerCase() === k || c.email.toLowerCase() === k);
      if (hit) auto[m.key] = hit.id;
    }
    setMap(auto);
  }, [plan.members, contacts]);
  const checklist = plan.cards.reduce((s, c) => s + c.checklist.length, 0);
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!plan.cards.length || !title.trim()) return;
        setBusy(true);
        try {
          const memberMap: Record<string, string | null> = {};
          for (const m of plan.members) memberMap[m.key] = map[m.key] || null;
          const r = await api.post<{ boardId: string; cards: number }>('/api/import/plan', { plan, title: title.trim(), source, memberMap, ...t.body });
          toast.info(`Importiert: ${n(r.cards, 'Karte', 'Karten')}.`);
          await openNewBoard(r.boardId, onClose);
        } catch (err) {
          toast.error((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      {extra}
      <div className="flex flex-col gap-2" aria-live="polite">
        <h3 className="text-sm font-semibold">Vorschau</h3>
        <p className="text-sm text-[var(--ink-soft)]">
          {plan.cards.length
            ? `${n(plan.columns.length, 'Spalte', 'Spalten')}, ${n(plan.cards.length, 'Karte', 'Karten')}, ${n(plan.labels.length, 'Label', 'Labels')}${checklist ? `, ${n(checklist, 'Checklistenpunkt', 'Checklistenpunkte')}` : ''}.`
            : 'Noch keine Karten erkannt.'}
        </p>
        {warnings.map((w) => (
          <p key={w} className="rounded-lg bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">
            {w}
          </p>
        ))}
        {plan.cards.length > 0 && <BoardPreview preview={preview} mode="board" />}
      </div>
      {plan.members.length > 0 && (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium">Personen zuordnen</legend>
          <p className="text-xs text-[var(--ink-soft)]">
            Zugeordnete Personen werden zuständig für ihre Karten und kommen als Bearbeiter ins Board. Ohne Zuordnung steht der Name in der Kartenbeschreibung.
          </p>
          {plan.members.map((m) => (
            <label key={m.key} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="min-w-32 flex-1 truncate">{m.name}</span>
              <select
                aria-label={`${m.name} zuordnen`}
                className={cx(fieldClass, 'w-auto min-w-48 flex-1')}
                value={map[m.key] ?? ''}
                onChange={(e) => setMap((x) => ({ ...x, [m.key]: e.target.value }))}
              >
                <option value="">nicht zuordnen</option>
                {contacts?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.id === me?.id ? `${c.name} (ich)` : c.name}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </fieldset>
      )}
      <label className="flex flex-col gap-1.5 text-sm font-medium">
        Name des neuen Boards
        <input
          className={fieldClass}
          required
          maxLength={120}
          value={title}
          onChange={(e) => {
            setTouched(true);
            setTitle(e.target.value);
          }}
        />
      </label>
      <TargetFields t={t} idPrefix={`imp-${source}`} />
      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={busy || !plan.cards.length || !title.trim()}>
          {busy ? 'Lege an …' : 'Board anlegen'}
        </Button>
      </div>
    </form>
  );
}

/* ---------- Aufgaben als Text ---------- */

function TextImport({ open, target, onClose }: { open: boolean; target?: Target | null; onClose: () => void }) {
  const [text, setText] = useState('');
  const parsed = useMemo(() => parseTextImport(text), [text]);
  const named = parsed.plan.title !== 'Importiertes Board' ? parsed.plan.title : '';
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 lg:grid-cols-[1fr_17rem]">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <label htmlFor="imp-text" className="text-sm font-medium">
              Deine Aufgaben
            </label>
            <button type="button" className="min-h-9 text-sm text-accent-fg underline-offset-2 hover:underline" onClick={() => setText(EXAMPLE)}>
              Beispiel einsetzen
            </button>
          </div>
          <textarea
            id="imp-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={11}
            spellCheck={false}
            placeholder={'## Offen\n- Erste Aufgabe\n- Zweite Aufgabe #Wichtig @3.11.\n## Erledigt\n[x] Schon fertig'}
            className="rounded-lg border border-[var(--line)] bg-[var(--panel)] p-3 font-mono text-sm leading-relaxed"
          />
        </div>
        <aside className="rounded-xl bg-[var(--column)] p-3 text-xs leading-relaxed" aria-label="So schreibst du die Liste">
          <h3 className="mb-1.5 text-sm font-semibold">So geht’s</h3>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <Syntax code="## Name" text="neue Spalte" />
            <Syntax code="- Titel" text="eine Karte je Zeile" />
            <Syntax code="  [ ] Punkt" text="eingerückt: Checkliste" />
            <Syntax code="  Notiz" text="eingerückt: Beschreibung" />
            <Syntax code="#Label" text="Label (mehrere möglich)" />
            <Syntax code="@3.11." text="Frist, auch @3.11.2027 14:00" />
            <Syntax code="[x] Titel" text="schon erledigt" />
            <Syntax code="# Name" text="Boardname (erste Zeile)" />
          </dl>
          <p className="mt-2 text-[var(--ink-soft)]">
            Listen aus Word, E-Mails oder Notizen einfach einfügen. Zeilen aus Excel oder Google Sheets (mit Überschriften wie „Aufgabe“, „Status“, „Fällig“)
            werden als Tabelle erkannt.
          </p>
        </aside>
      </div>
      {text.trim() && <PlanResult parsed={parsed} source="text" defaultTitle={named} open={open} target={target} onClose={onClose} />}
    </div>
  );
}

export function Syntax({ code, text }: { code: string; text: string }) {
  return (
    <>
      <dt>
        <code className="rounded bg-[var(--panel)] px-1 font-mono whitespace-pre">{code}</code>
      </dt>
      <dd className="text-[var(--ink-soft)]">{text}</dd>
    </>
  );
}

/* ---------- CSV / Excel ---------- */

export function FilePick({ accept, label, file, onFile, hint }: { accept: string; label: string; file: File | null; onFile: (f: File | null) => void; hint: string }) {
  return (
    <label
      className={cx(
        'flex min-h-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed p-4 text-center text-sm',
        file ? 'border-accent bg-accent-soft/40' : 'border-[var(--line)] hover:bg-black/[0.02] dark:hover:bg-white/5',
      )}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onFile(e.dataTransfer.files[0] ?? null);
      }}
    >
      <input type="file" accept={accept} className="sr-only" aria-label={label} onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
      <span className="inline-flex items-center gap-2 font-medium">
        <Upload size={18} aria-hidden /> {file ? file.name : label}
      </span>
      <span className="text-xs text-[var(--ink-soft)]">{hint}</span>
    </label>
  );
}

export interface SheetChoice {
  name: string;
  parsed: ParsedImport;
}

/** Planner-Export erkennen (Spalte „Bucketname“ / „Bucket Name“) – nur für die Herkunft im Verlauf */
export const isPlanner = (p: ParsedImport) => !!p.mapping?.some((m) => /bucket/i.test(m.header));

/** Excel- oder CSV-Datei lesen (auch für „Karten importieren …“ im Board, 0.88) */
export async function readImportFile(f: File): Promise<{ sheets: SheetChoice[]; best: number } | { error: string }> {
  if (f.size > 10 * 1024 * 1024) return { error: 'Die Datei ist zu groß (max. 10 MB).' };
  if (/\.xls$/i.test(f.name)) return { error: 'Das alte Excel-Format (.xls) kann KaBa nicht lesen. Bitte in Excel als .xlsx oder als „CSV UTF-8“ speichern.' };
  try {
    let found: SheetChoice[];
    if (/\.xlsx$/i.test(f.name)) {
      const all = await readXlsx(await f.arrayBuffer());
      found = all.map((s) => ({ name: s.name, parsed: parseTableImport(s.rows) })).filter((s) => s.parsed.plan.cards.length > 0);
    } else {
      const p = parseCsvImport(await readText(f));
      found = p.plan.cards.length ? [{ name: '', parsed: p }] : [];
    }
    if (!found.length)
      return { error: 'In der Datei wurden keine Karten gefunden. Es braucht eine Zeile mit Überschriften wie „Titel“ (oder „Aufgabenname“) und „Spalte“ (oder „Bucket“, „Status“).' };
    // Das Blatt mit den meisten Karten vorwählen
    return { sheets: found, best: found.reduce((best, s, i) => (s.parsed.plan.cards.length > found[best]!.parsed.plan.cards.length ? i : best), 0) };
  } catch (err) {
    return { error: err instanceof XlsxError ? err.message : 'Die Datei ließ sich nicht lesen.' };
  }
}

function CsvImport({ open, target, onClose }: { open: boolean; target?: Target | null; onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [sheets, setSheets] = useState<SheetChoice[] | null>(null);
  const [sheet, setSheet] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const choose = async (f: File | null) => {
    setFile(f);
    setSheets(null);
    setSheet(0);
    setError(null);
    if (!f) return;
    setReading(true);
    try {
      const r = await readImportFile(f);
      if ('error' in r) setError(r.error);
      else {
        setSheets(r.sheets);
        setSheet(r.best);
      }
    } finally {
      setReading(false);
    }
  };
  const current = sheets?.[sheet];
  const xlsx = !!file && /\.xlsx$/i.test(file.name);
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[var(--ink-soft)]">
        Eine Zeile pro Karte – als <strong>Excel-Datei (.xlsx)</strong> oder CSV. Erkannt werden Überschriften wie <strong>Titel</strong>,{' '}
        <strong>Spalte</strong> bzw. Status oder Bucket, <strong>Beschreibung</strong>, <strong>Labels</strong>, <strong>Fällig</strong>,{' '}
        <strong>Start</strong>, <strong>Priorität</strong>, <strong>Zuständig</strong>, <strong>Checkliste</strong> und <strong>Erledigt</strong> – auch auf
        Englisch.
      </p>
      <p className="text-sm text-[var(--ink-soft)]">
        <strong>Aus Microsoft Planner:</strong> im Plan oben auf „…“ → „Plan in Excel exportieren“ und die Datei hier auswählen. Buckets werden zu Spalten,
        Fortschritt „Abgeschlossen“ zu erledigten Karten.
      </p>
      <p className="text-sm text-[var(--ink-soft)]">
        <strong>Aus Asana:</strong> im Projekt „…“ → „Exportieren/Drucken“ → „CSV“. <strong>Aus Jira:</strong> in der Vorgangssuche „Exportieren“ → „CSV (alle
        Felder)“. Abschnitte bzw. Status werden zu Spalten, Unteraufgaben zur Checkliste, „Blockiert durch“ zu Abhängigkeiten.
      </p>
      <p className="text-sm text-[var(--ink-soft)]">
        <strong>Aus Todoist:</strong> im Projekt „…“ → „Als Vorlage exportieren“ → „Als Datei exportieren“ (CSV). <strong>Aus TickTick:</strong>{' '}
        Einstellungen → Konto → „Datensicherung erstellen“ (CSV). Abschnitte, Listen bzw. Kanban-Spalten werden zu Spalten, Unteraufgaben und
        Checklisten zur Checkliste.
      </p>
      <FilePick
        accept=".xlsx,.csv,.tsv,.txt,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        label="Excel- oder CSV-Datei auswählen"
        hint="oder hierher ziehen"
        file={file}
        onFile={(f) => void choose(f)}
      />
      {reading && <p className="text-sm text-[var(--ink-soft)]">Lese Datei …</p>}
      {error && (
        <p role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
          {error}
        </p>
      )}
      {sheets && sheets.length > 1 && (
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Tabellenblatt
          <select className={fieldClass} value={sheet} onChange={(e) => setSheet(Number(e.target.value))}>
            {sheets.map((s, i) => (
              <option key={s.name} value={i}>
                {s.name} ({n(s.parsed.plan.cards.length, 'Karte', 'Karten')})
              </option>
            ))}
          </select>
        </label>
      )}
      {current && (
        <PlanResult
          key={`${file?.name}|${sheet}`}
          parsed={current.parsed}
          source={current.parsed.source ?? (isPlanner(current.parsed) ? 'planner' : xlsx ? 'xlsx' : 'csv')}
          defaultTitle={file?.name.replace(/\.[^.]+$/, '') ?? ''}
          open={open}
          target={target}
          onClose={onClose}
          extra={
            current.parsed.mapping?.length ? (
              <div className="flex flex-wrap items-center gap-1.5 text-sm">
                <span className="text-[var(--ink-soft)]">Erkannt:</span>
                {current.parsed.mapping.map((m) => (
                  <span key={m.field} className="rounded-full bg-accent-soft px-2.5 py-0.5 text-xs">
                    {CSV_FIELD_LABELS[m.field]} ← „{m.header}“
                  </span>
                ))}
              </div>
            ) : null
          }
        />
      )}
    </div>
  );
}

/* ---------- KaBa-Datei ---------- */

function KabaImport({ open, target, onClose }: { open: boolean; target?: Target | null; onClose: () => void }) {
  const t = useBoardTarget(open, target);
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<KabaBoardFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [withCards, setWithCards] = useState(true);
  const [busy, setBusy] = useState(false);
  const summary = useMemo(
    () => (data ? templateSummary({ id: 'datei', name: data.title, description: data.description ?? '', data: data.template }, { builtIn: false, shared: false, createdBy: null }) : null),
    [data],
  );
  const choose = async (f: File | null) => {
    setFile(f);
    setData(null);
    setError(null);
    if (!f) return;
    try {
      const d: unknown = JSON.parse(await f.text());
      if (!isKabaBoardFile(d)) throw new Error('Das ist keine KaBa-Datei. Du bekommst sie im Board-Menü unter „Als Datei exportieren“.');
      setData(d);
      setTitle(d.title);
    } catch (err) {
      setError(err instanceof SyntaxError ? 'Die Datei ist kein gültiges JSON.' : (err as Error).message);
    }
  };
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!data || !title.trim()) return;
        setBusy(true);
        try {
          const b = await api.post<Board>('/api/import/kaba', { file: data, title: title.trim(), withCards, ...t.body });
          await openNewBoard(b.id, onClose);
        } catch (err) {
          toast.error((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <p className="text-sm text-[var(--ink-soft)]">
        Überträgt ein Board aus einer anderen KaBa-Installation oder als Kopie: Spalten mit Regeln, Labels, eigene Felder, Kartenvorlagen, Automationen und die
        offenen Karten. Die Datei bekommst du im Board-Menü unter „Als Datei exportieren“.
      </p>
      <FilePick accept=".json,application/json" label="KaBa-Datei auswählen" hint="Endung .kaba.json" file={file} onFile={(f) => void choose(f)} />
      {error && (
        <p role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
          {error}
        </p>
      )}
      {summary && (
        <>
          <p className="text-sm">
            <strong>„{data!.title}“</strong>: {n(summary.columns.length, 'Spalte', 'Spalten')}, {n(summary.cardCount, 'Karte', 'Karten')},{' '}
            {n(summary.preview.labels.length, 'benanntes Label', 'benannte Labels')}, {n(summary.automationCount, 'Automation', 'Automationen')}
            {summary.cardTemplateCount ? `, ${n(summary.cardTemplateCount, 'Kartenvorlage', 'Kartenvorlagen')}` : ''}.
          </p>
          <BoardPreview preview={summary.preview} mode="board" />
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Name des neuen Boards
            <input className={fieldClass} required maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <TargetFields t={t} idPrefix="imp-kaba" />
          {summary.cardCount > 0 && (
            <label className="flex min-h-11 items-center gap-3 text-sm">
              <input type="checkbox" checked={withCards} onChange={(e) => setWithCards(e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
              {n(summary.cardCount, 'Karte', 'Karten')} mit übernehmen
            </label>
          )}
          <p className="text-xs text-[var(--ink-soft)]">
            Nicht enthalten sind Personen, Kommentare, Anhänge, Termine und der Verlauf. Regeln, die einer bestimmten Person etwas zuweisen, werden ausgelassen.
          </p>
        </>
      )}
      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={!data || !title.trim() || busy}>
          {busy ? 'Lege an …' : 'Board anlegen'}
        </Button>
      </div>
    </form>
  );
}
